/* eslint-disable @typescript-eslint/no-unsafe-argument */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC } from 'react';
import { uniqueId } from 'lodash-es';
import { TMap, TMethods, TMethodsMap } from './types';
import makeImperativeComponent from './makeImperativeComponent';
import { TId } from '../../components/common/generic/tree/types';

type TSingleMethodsMap<
  Methods extends TMethods<any>,
  MethodsMap extends TMethodsMap<Methods>,
> = {
  [Name in keyof MethodsMap]: MethodsMap[Name] extends (
    id: TId,
    ...args: infer Action
  ) => unknown
    ? (...args: Action) => unknown
    : never;
};

export default function makeSingleImperativeComponent<
  ComponentProps extends TMap,
  Events extends TMap = TMap,
>() {
  return function SingleImperativeComponent<
    State extends TMap,
    Methods extends TMethods<State>,
  >({
    initialState,
    methods,
    Component,
  }: {
    initialState?: State;
    methods?: Methods;
    Component: FC<Omit<ComponentProps, 'id'> & State>;
  }): [
    TSingleMethodsMap<Methods, TMethodsMap<Methods>>,
    <K extends keyof Events>(ev: K, args: Events[K]) => void,
    FC<ComponentProps>,
  ] {
    const id = uniqueId();

    const [actualMethods, originalFireEvent, ImperativeComponent] =
      makeImperativeComponent<ComponentProps, Events>()({
        initialState,
        methods,
        Component,
      });

    const fireEvent: <K extends keyof Events>(
      ev: K,
      args: Events[K],
    ) => void = (ev, args) => originalFireEvent(id, ev, args);

    const newMethods: TSingleMethodsMap<
      Methods,
      TMethodsMap<Methods>
    > = {} as TSingleMethodsMap<Methods, TMethodsMap<Methods>>;

    Object.entries(actualMethods).forEach(([name, method]) => {
      (newMethods[name as keyof typeof newMethods] as any) = (...args: any) =>
        (method as (id: TId, ...args: any) => unknown)(id, ...args);
    });

    return [
      newMethods,
      fireEvent,
      (props: ComponentProps) => <ImperativeComponent {...props} id={id} />,
    ];
  };
}
